import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { Combine, GitCompareArrows, Maximize2, ListOrdered } from "lucide-react";

/** Phase 35 — one-screen welcome plaque, shown once per account (server-tracked).
    Phase 38 — messaging + CTA adapts to Verified Explorer vs. approved
    Quick Reply. Explorers land on /innovation (browse), approved users on
    /bot (live). */
export default function WelcomePlaque() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [gone, setGone] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data: access } = useQuery({
    queryKey: ["early-access-me"],
    queryFn: async () => {
      const { data } = await api.get("/early-access/me");
      return data;
    },
    enabled: !!user,
    staleTime: 60 * 1000,
    retry: 0,
  });

  if (!user || user.welcome_seen !== false || gone) return null;

  const quickReplyAllowed = access ? access.quick_reply_allowed : true;

  const start = async () => {
    setBusy(true);
    try {
      await api.post("/auth/welcome-seen");
    } catch {
      /* non-blocking */
    }
    setGone(true);
    nav(quickReplyAllowed ? "/bot" : "/innovation");
  };

  const lines = [
    {
      icon: Combine,
      text: "MindStein Lab fuses ideas across science, business, and philosophy into novel, actionable insights.",
    },
    {
      icon: GitCompareArrows,
      text: "Four synthesis modes: COMBINE · COMPARE · EXPAND · PRIORITIZE.",
    },
    {
      icon: ListOrdered,
      text: "Every output is grounded in credibility-ranked sources (scored 1-10, always linked).",
    },
    {
      icon: Maximize2,
      text: quickReplyAllowed
        ? "Original reasoning is always labeled [SYNTHESIS] — you see exactly where facts end and thinking begins."
        : "You're a Verified Explorer — browse the Intelligence Layer, example library and roadmap. Live Quick Reply is invite-only right now.",
    },
  ];

  return (
    <div
      data-testid="welcome-plaque"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center overflow-hidden"
    >
      <div className="max-w-xl w-full mx-4 border border-emerald-400/40 bg-[#0A0A0A] p-8 sm:p-10 space-y-6">
        <div className="space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-400">
            FIRST_LOGIN · INITIALIZED
          </div>
          <h1 className="text-2xl sm:text-3xl font-mono tracking-tight text-white leading-snug">
            {quickReplyAllowed
              ? "Welcome to MindStein Lab — Your Cross-Domain Intelligence Terminal"
              : "Welcome to MindStein Lab — you're a Verified Explorer"}
          </h1>
        </div>
        <ul className="space-y-3">
          {lines.map((l, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-zinc-300 leading-relaxed">
              <l.icon size={16} className="text-emerald-400 shrink-0 mt-0.5" />
              <span>{l.text}</span>
            </li>
          ))}
        </ul>
        <button
          data-testid="welcome-run-first-query"
          onClick={start}
          disabled={busy}
          className="w-full h-11 bg-emerald-400 text-black font-mono uppercase tracking-[0.2em] text-[12px] font-bold hover:bg-emerald-300 disabled:opacity-60"
        >
          {busy ? "OPENING…" : (quickReplyAllowed ? "Run Your First Query →" : "Explore the Lab →")}
        </button>
      </div>
    </div>
  );
}
